import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Collapse from '@material-ui/core/Collapse';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import { Colors } from '@silvergatedelivery/constants';
import { useCache } from 'CacheProvider';
import cache from 'utilities/cache';
import Place from 'components/Map/Place';
import LinkButton from 'components/Table/LinkButton';

const useStyles = makeStyles((theme) => ({
  root: {
    'width': '100%',
    'position': 'relative',
    'transition': 'all 0.3s ease-in-out',
    '&.collapsed': {
      'width': theme.spacing(3),
      'overflow': 'hidden',
      '& $listSubHeader': {
        'writingMode': 'vertical-rl',
        'height': '100%',
        'padding': theme.spacing(1, 0),
        'minHeight': '150px',
        'display': 'flex',
        'justifyContent': 'flex-start',
        'backgroundColor': theme.palette.background.default,
      },
      '& $header': {
        'transform': 'translateX(-10px)',
        'width': 'fit-content',
        'gap': theme.spacing(0.5),
      },
    },
  },
  listSubHeader: {
    fontWeight: 700,
    borderBottom: '1px solid #E5E5E5',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  actions: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 10,
    display: 'flex',
    flexDirection: 'row',
  },
  actionsSpace: {
    width: 8,
    display: 'inline-block',
  },
  button: {
    margin: theme.spacing(2),
  },
  map: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export default function InfoCard({
  children,
  title,
  link,
  mappings = [],
  data = {},
  collapse = false,
  expand: inExpand,
  hideExpand = false,
  editButton,
  onUpdate,
  collapseDirection = 'up',
  onCollapseChange,
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(!collapse);
  const [expand, setExpand] = useState(hideExpand ? true : inExpand);
  const { appGroup } = useCache();
  const facilityMode = appGroup === 'FacilityAdmins';
  const clientId = cache.get('app:facilityId');
  let isDataOwner = true;
  if (facilityMode && data.clientId) {
    isDataOwner = data.clientId === clientId;
  }

  const handleClick = () => {
    const newOpen = !open;
    setOpen(newOpen);
    onCollapseChange?.(newOpen);
  };

  const filteredMappings = mappings.filter((item) => !item.hide);

  const hasExpandFields = filteredMappings.some((item) => item.expand);

  const renderContent = (item) => {
    const content = (item.format ? item.format(data[item.key]) :
      data[item.key] === true ? '是' : data[item.key] === false ? '否' : data[item.key]) || '無';
    return (
      <React.Fragment>
        {content}
        {content !== '無' && content !== '無 (無) 無' && typeof content === 'string' && content.length > 4 &&
          <CopyToClipboard
            text={content}
          >
            <FileCopyIcon style={{ color: Colors.light, fontSize: 16, cursor: 'pointer', marginLeft: 8 }} />
          </CopyToClipboard>
        }
      </React.Fragment>);
  };

  return (
    <Paper>
      <List className={`${classes.root} ${!open && collapseDirection === 'left' ? 'collapsed' : ''}`} dense={true} disablePadding={true}>
        {title &&
        <ListSubheader
          component="li"
          className={classes.listSubHeader}
          disableSticky={true}
          onClick={handleClick}
        >
          <div className={classes.header}>
            {collapseDirection === 'left' ?
              <ExpandMore /> :
              (open ? <ExpandMore /> : <KeyboardArrowRightIcon />)
            }
            {title}
          </div>
        </ListSubheader>}
        {(open || collapseDirection === 'up')&&
        <div className={classes.actions}>
          {isDataOwner && editButton && editButton({ item: data, onUpdate: (data) => onUpdate && onUpdate(data) })}
          <div className={classes.actionsSpace} />
          {isDataOwner && link && <LinkButton path={link || ''} label="在新頁面開啟" newTab={true} disabled={!link} /> }
        </div>}

        <Collapse in={open} timeout="auto" unmountOnExit>
          {children}
          {filteredMappings.filter((x) => expand ? true : !x.expand).map((item, index)=>(
            <React.Fragment key={index} >
              <ListItem dense={true}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ variant: 'body2', color: 'textSecondary', noWrap: true }}
                  secondary={renderContent(item)}
                  secondaryTypographyProps={{
                    variant: 'body1',
                    color: 'textPrimary',
                    style: { wordBreak: 'break-all' },
                  }}
                />
              </ListItem>
              {item.showMap ? <div className={classes.map}><Place address={data[item.key]} /></div> : null}
            </React.Fragment>
          ))}
        </Collapse>
      </List>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {!hideExpand && hasExpandFields &&
        <Grid container justifyContent="center">
          <Button
            className={classes.button}
            onClick={() => setExpand(!expand)}
            startIcon={expand ? <ExpandLessIcon color="disabled"/> : <ExpandMoreIcon color="disabled"/>}
          >
            <Typography color="textSecondary" variant="body2" component="p">
              {expand ? '顯示部分重要資料' : '顯示全部'}
            </Typography>
          </Button>
        </Grid>}
      </Collapse>
    </Paper>
  );
}

InfoCard.propTypes = {
  children: PropTypes.any,
  title: PropTypes.string,
  link: PropTypes.string,
  mappings: PropTypes.array,
  data: PropTypes.object,
  expand: PropTypes.bool,
  collapse: PropTypes.bool,
  hideExpand: PropTypes.bool,
  editButton: PropTypes.func,
  onUpdate: PropTypes.func,
  collapseDirection: PropTypes.oneOf(['up', 'left']),
  onCollapseChange: PropTypes.func,
};
